import React, { useEffect, useState, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import TranscriptHeader from './TranscriptHeader';
import ProjectContext from 'contexts/ProjectContext';
import {BetaModalProvider, useModal} from '../modal/BetaModalProvider';
import { useTranscriptName } from 'hooks/transcripts/queries';
import { useUpdateTranscriptName, useDeleteTranscript } from 'hooks/transcripts/mutations';
import { useExportTranscriptMutation } from 'hooks/exports/mutations';
import { useHasAICoding } from 'hooks/feature-flags/queries';
import { useAIChat } from '../contexts/AIChatContext';

const TransriptHeaderWithModal = (props) => {
    const { openModal, closeModal, isModalOpen } = useModal();
    const { hasAICoding: featureFlagAICoding, isLoading } = useHasAICoding();

    useEffect(() => {
        // Don't do anything while loading
        if (isLoading) {
            return;
        }

        // if deductive modal is not shown, we do not need to show the beta modal
        if ( !props.showDeductiveModal ) {
            closeModal();
            return;
        }

        // if deductive modal is shown, we need to check if the user has the feature flag, if not, show the beta modal
        if (featureFlagAICoding === false) {
            openModal();
        } else if (featureFlagAICoding === true) { // if the user has the feature flag, the beta modal should not be showing
            closeModal()
        }
    }, [featureFlagAICoding, isLoading, props.showDeductiveModal, openModal, closeModal]);

    const aiCodings = (e) => {
        props.setShowDeductiveModal(true);
    }

    const hideAiCodings = (e) => {
        props.setShowDeductiveModal(false);
    }

    return <TranscriptHeader
                {...props}
                isBetaModalOpen={isModalOpen}
                showDeductiveModal={props.showDeductiveModal && !!featureFlagAICoding}
                aiCodings={aiCodings}
                hideAiCodings={hideAiCodings}
            />
}

const TranscriptHeaderContainer = (props) => {
    const { transcriptID: transcriptIDParam } = useParams();
    const history = useHistory();
    const [showDeductiveModal, setShowDeductiveModal] = useState(false);
    const projectContext = useContext(ProjectContext);
    const projectID = ((projectContext || {}).project || {}).id;
    const [showModal, setShowModal] = useState(false);
    const transcriptID = transcriptIDParam ? Number(transcriptIDParam) : null;
    
    // Use lightweight hook to get just the transcript name for display
    const { data: transcript } = useTranscriptName(transcriptID);
    const title = (transcript?.name || '').trim();
    
    // Use the mutation for updating transcript name
    const updateTranscriptMutation = useUpdateTranscriptName(projectID);
    
    // Use the mutation for deleting transcript
    const deleteTranscriptMutation = useDeleteTranscript(projectID);
    
    // Use the mutation for exporting transcript - will fetch data on demand
    const exportTranscriptMutation = useExportTranscriptMutation();

    const printTranscript = () => {
        window.open(`/transcripts/${transcriptID}/print`);
    }

    const exportTranscript = () => {
        // Pass transcript ID - mutation will fetch the data when needed
        exportTranscriptMutation.mutate(transcriptID);
    }

    const cancelDelete = (e) =>
    {
        setShowModal(false);
    }

    const deleteModalClicked = (e) =>
    {
        setShowModal(true);
    }
    
    const onTitleChange = (title) => {
        const trimmedTitle = title ? title.trim() : '';
        if ( trimmedTitle.length > 0 )
        {
          updateTranscriptMutation.mutate({ 
            transcriptId: transcriptID, 
            name: trimmedTitle 
          });
        }
    }

    const confirmDelete = () =>
    {
      setShowModal(false);
      
      // Delete the transcript and navigate to project page
      deleteTranscriptMutation.mutate(transcriptID, {
        onSuccess: () => {
          history.push(`/projects/${projectID}`);
        },
        onError: (error) => {
          console.error('Failed to delete transcript:', error);
        }
      });
    }
  
    return <BetaModalProvider onReject={()=>{setShowDeductiveModal(false)}}>
        <TransriptHeaderWithModal
            setShowDeductiveModal={setShowDeductiveModal}
            showDeductiveModal={showDeductiveModal}
            onTitleChange={onTitleChange}
            confirmDelete={confirmDelete}
            projectID={projectID}
            cancelDelete={cancelDelete}
            deleteModalClicked={deleteModalClicked}
            showModal={showModal}
            exportTranscript={exportTranscript}
            printTranscript={printTranscript}
            title={title}
            transcriptID={transcriptID}
            {...props}
            />
    </BetaModalProvider>
}

export default TranscriptHeaderContainer;